/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 12/12/2023
 */

import {
  Backdrop,
  Button,
  CircularProgress,
  FormHelperText,
  Paper,
  TextField,
  Typography,
  Grid,
} from "@mui/material";
import React, { useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string, ref } from "yup";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useNavigate } from 'react-router-dom';
import {
  useGetProfileDetailsQuery,
  useUpdateProfileMutation,
  useDeleteProfileMutation,
} from "../Store/Services/ProfileService";

import styles from "./Profile.module.css";

const schema = object({
  firstName: string().trim().required("First Name is required"),
  // lastName: string().trim().required("Last Name is required"),
  // userName: string().trim().required("User Name is required"),
  email: string()
    .trim()
    .email("Please provide a valid email")
    .required("Email is required"),
  // telephone: string(),
  mobile: string().required("Mobile is required"),
}).required();

const ProfilePage = () => {
    const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const { data, isSuccess, isLoading } = useGetProfileDetailsQuery();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteProfileMutation(); 
  const [updateUser, { isLoading: isUpdating }] = useUpdateProfileMutation();

  useEffect(() => {
    if (isSuccess && data) {
      const profile = data;

      reset({
        firstName: profile?.firstName || "",
        lastName: profile?.lastName || "",
        userName: profile?.userName || "",
        email: profile?.email || "",
        telephone: profile?.telephone || "",
        mobile: profile?.mobile || "",
      });
    }
  }, [data, reset, isSuccess]);


  const onSubmit = async (formData) => {
    try {
        const updatedUser = await updateUser(formData).unwrap();
        toast.success("User Details Updated Successfully!", {
            position: "bottom-right",
        });
        reset(updatedUser);
    } catch (error) {
        console.error("Error updating user:", error); 
        toast.error("Error, something went wrong", { position: "bottom-right" });
    }
  };


  const handleDelete = async () => {
    try {
      await deleteUser().unwrap(); 
      toast.success("Profile deleted successfully!", { position: "bottom-right" });
      navigate('/register'); 
    } catch (error) {
      toast.error("Error deleting profile", { position: "bottom-right" });
      console.error(error)
    }
  };

  return (
    <div className={styles.container}>
      <Paper className={styles.paper}>
        {isLoading && (
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={isLoading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        <div className={styles.addAddressContainer}>
          <Typography fontWeight={600} variant={"h6"}>
            Personal Details
          </Typography>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className={styles.form}
          autoComplete="off"
        >
          <div className={styles.formItem}>
            <label className={styles.formLabel}> Name </label>
            <TextField
              className={styles.formText}
              multiline
              rows={1}
              {...register("firstName")}
              size="small"
              error={!!errors.firstName}
              fullWidth
            />
            {errors.firstName && (
              <FormHelperText error>
                {errors.firstName?.message}  
              </FormHelperText>
            )}
          </div>
          <div className={styles.formItem}>
            <label className={styles.formLabel}> Email </label>
            <TextField
              className={styles.formText}
              multiline
              rows={1}
              {...register("email")}
              size="small"
              error={!!errors.email}
              fullWidth
            />
            {errors.email && (
              <FormHelperText error> 
                {errors.email?.message} 
              </FormHelperText>
            )}
          </div>
          <div className={styles.formItem}>
            <label className={styles.formLabel}> Mobile </label>
            <TextField
              className={styles.formText}
              multiline
              rows={1}
              {...register("mobile")}
              error={!!errors.mobile}
              size="small"
              fullWidth
            />
            {errors.mobile && (
              <FormHelperText error> 
                {errors.mobile?.message}  
              </FormHelperText>
            )}
          </div>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button className={styles.button} type="submit" variant="contained" fullWidth>
                Save
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                className={styles.button}
                variant="outlined"
                color="error"
                fullWidth
                onClick={handleDelete} 
              >
                Delete Account
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </div>
  );
};

export default ProfilePage;